import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { contentLoadingChange } from "../../../Reducer/componentSlice";
import Handler from "../../../Helpers/Handler";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { FaCheckCircle, FaPassport, FaRegIdBadge } from "react-icons/fa";
import { GoShieldCheck } from "react-icons/go";
import { BsPersonVcard } from "react-icons/bs";
import { useAuth } from "../../../Contexts/AuthenticateProvider";

const ThirdDocument = ({ customerDetails, onVerified }) => {
    const dispatch = useDispatch();
    const { hasPermission } = useAuth();
    const canPassport = hasPermission("customer.kyc.passport.submit");
    const canVoter = hasPermission("customer.kyc.voter.submit");

    const [view, setView] = useState(canPassport ? "passport" : "voter");
    const [loading, setLoading] = useState(false);

    const [voterId, setVoterId] = useState("");

    const [passportData, setPassportData] = useState({
        passportNo: "",
        name: "",
        dob: null,
    });

    const changeDocument = (type) => {
        setView(type);
        setVoterId("");

        setPassportData((prev) => ({
            ...prev,
            passportNo: "",
        }));
    };

    const voterSubmit = async () => {
        if (!canVoter) {
            toast.error("You don't have permission to submit Voter ID verification");
            return;
        }
        if (!voterId.trim()) {
            toast.error("Enter Voter ID");
            return;
        }

        try {
            setLoading(true);
            dispatch(contentLoadingChange(true));

            const response = await Handler({
                method: "patch",
                url: `/staff/customer/${customerDetails?.customerId}/kyc/voter/verify`,
                data: {
                    voterId: voterId.toUpperCase(),
                },
            });

            if (response?.success) {
                toast.success(
                    response?.data?.message ||
                    "Voter ID data fetched, pending verification"
                );

                dispatch(contentLoadingChange(false));

                setTimeout(() => {
                    setLoading(false);
                    onVerified?.();
                }, 1200);
            } else {
                toast.error(
                    response?.data?.message || "Submission failed"
                );
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
            dispatch(contentLoadingChange(false));
        }
    };

    const passportSubmit = async () => {
        if (!canPassport) {
            toast.error("You don't have permission to submit Passport verification");
            return;
        }
        if (!passportData.passportNo.trim()) {
            toast.error("Enter Passport File Number");
            return;
        }

        if (!passportData.dob) {
            toast.error("DOB is required");
            return;
        }

        try {
            setLoading(true);
            dispatch(contentLoadingChange(true));

            const response = await Handler({
                method: "patch",
                url: `/staff/customer/${customerDetails?.customerId}/kyc/passport/verify`,
                data: {
                    passportNo:
                        passportData.passportNo.toUpperCase(),
                    dob: passportData.dob.format("DD-MM-YYYY"),
                },
            });

            if (response?.success) {
                toast.success(response?.data?.message || "Passport data fetched, pending verification");
                dispatch(contentLoadingChange(false));
                setTimeout(() => {
                    setLoading(false);
                    onVerified?.();
                }, 1200);
            } else {
                toast.error(
                    response?.data?.message || "Submission failed"
                );
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
            dispatch(contentLoadingChange(false));
        }
    };

    useEffect(() => {
        const parseDOB = (dateStr) => {
            if (!dateStr) return null;

            const parts = dateStr.split("-");

            if (parts.length !== 3) return null;

            const [day, month, year] = parts;

            return new DateObject({
                day: Number(day),
                month: Number(month),
                year: Number(year),
            });
        };

        const dlDob =
            customerDetails?.kyc?.dl?.dlData?.dateOfBirth;

        const aadhaarDob =
            customerDetails?.kyc?.aadhaar?.aadhaarDetails
                ?.proofOfIdentity?.dob;

        const finalDob = dlDob
            ? parseDOB(dlDob)
            : aadhaarDob
                ? parseDOB(aadhaarDob)
                : null;

        setPassportData({
            passportNo: "",
            name: customerDetails?.firstName || "",
            dob: finalDob,
        });
    }, [customerDetails]);

    return (
        <>
            <div className="row g-3 mb-4">

               {canPassport ? (
                    <div className="col-md-6">

                        <button type="button" disabled={loading} onClick={() => changeDocument("passport")} className={`w-100 border rounded-3 p-3 text-start bg-white transition ${view === "passport" ? "border-primary shadow-sm" : "border-light-subtle"}`} >

                            <div className="d-flex align-items-center gap-3">
                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${view === "passport" ? "bg-primary text-white" : "bg-light text-secondary"}`} style={{ width: "44px", height: "44px", minWidth: "44px", }} ><FaPassport /></div>
                                <div className="flex-grow-1">
                                    <div className="fw-semibold"> Passport </div>
                                    <div className="small text-muted"> Verify using passport details </div>
                                </div>
                                {view === "passport" && (<FaCheckCircle />)}
                            </div>
                        </button>

                    </div>
                ) : null}

                {canVoter ? (
                    <div className="col-md-6">
                        <button
                            type="button"
                            disabled={loading}
                            onClick={() => changeDocument("voter")}
                            className={`w-100 border rounded-3 p-3 text-start bg-white transition ${view === "voter" ? "border-primary shadow-sm" : "border-light-subtle"}`}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${view === "voter" ? "bg-primary text-white" : "bg-light text-secondary"}`} style={{ width: "44px", height: "44px", minWidth: "44px", }} > <FaRegIdBadge /> </div>
                                <div className="flex-grow-1">
                                    <div className="fw-semibold"> Voter ID </div>
                                    <div className="small text-muted"> Verify using Voter ID number </div>
                                </div>
                                {view === "voter" && (<FaCheckCircle />)}
                            </div>
                        </button>
                    </div>
                ) : null}

                {!canPassport && !canVoter ? (
                    <div className="col-12 text-muted small">
                        You don't have permission to submit a Voter ID or Passport verification.
                    </div>
                ) : null}
            </div>

            <div className="border rounded-3 p-4 bg-light-subtle">

                {view === "passport" ? (
                    <>
                        <div className="mb-4">

                            <label className="form-label fw-semibold">
                                Passport File Number
                            </label>

                            <div className="input-group">

                                <input
                                    type="text"
                                    className="form-control"
                                    disabled={loading}
                                    value={passportData.passportNo}
                                    onChange={(e) =>
                                        setPassportData((prev) => ({
                                            ...prev,
                                            passportNo:
                                                e.target.value.toUpperCase(),
                                        }))
                                    }
                                    placeholder="Enter passport file number"
                                    autoComplete="off"
                                />

                            </div>

                            <div className="form-text">
                                Enter the Passport File Number (not the passport number) exactly as printed on the passport file.
                            </div>

                        </div>

                        <div className="mb-4">

                            <label className="form-label fw-semibold">
                                Date of Birth
                            </label>

                            <div className="position-relative">

                                <DatePicker
                                    value={passportData.dob}
                                    maxDate={new Date()}
                                    minDate={new Date(1940, 0, 1)}
                                    format="DD/MM/YYYY"
                                    disabled={loading}
                                    inputClass="form-control"
                                    onChange={(val) =>
                                        setPassportData((prev) => ({
                                            ...prev,
                                            dob: val,
                                        }))
                                    }
                                />

                            </div>

                            <div className="form-text">
                                Date of birth is pre-filled when available
                                from existing KYC information.
                            </div>

                        </div>

                        <div className="d-flex justify-content-end">

                            <button
                                type="button"
                                className="btn btn-primary px-4"
                                onClick={passportSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        <GoShieldCheck className="me-2" />
                                        Verify Passport
                                    </>
                                )}
                            </button>

                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-4">

                            <label className="form-label fw-semibold">
                                Voter ID Number
                            </label>

                            <div className="input-group">

                                <span className="input-group-text bg-white">
                                    <BsPersonVcard />
                                </span>

                                <input
                                    type="text"
                                    className="form-control text-uppercase"
                                    autoComplete="off"
                                    disabled={loading}
                                    value={voterId}
                                    onChange={(e) =>
                                        setVoterId(
                                            e.target.value.toUpperCase()
                                        )
                                    }
                                    placeholder="Enter voter ID number"
                                />

                            </div>

                            <div className="form-text">
                                Enter the customer's valid Voter ID number.
                            </div>

                        </div>

                        <div className="d-flex justify-content-end">

                            <button
                                type="button"
                                className="btn btn-primary px-4"
                                onClick={voterSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        <GoShieldCheck className="me-2" />
                                        Verify Voter ID
                                    </>
                                )}
                            </button>

                        </div>
                    </>
                )}

            </div>
        </>
    );
};

export default ThirdDocument;
